import { inject, Injectable } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import App from '@app/app';
import type { LookImage } from '@app/interface/image/look-image.interface';
import type { Subject } from '@app/interface/subject/subject.interface';
import { isIrisUrl } from '@app/service/image/image-url';
import { firstMasterEventLook, masterEventLookByJobKey } from '@app/service/image/master-event-look';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';

/** A single (image, token) pair sourced from somewhere on the subject. */
type LookCandidate = {
  viewURL?: string | null;
  bearerTokenURL?: string | null;
};

@Injectable({ providedIn: 'root' })
export class SubjectLookResolver {
  private readonly subjectStore = inject(angularjs.SubjectStore);
  private readonly jobStore = inject(angularjs.JobStore);

  resolve(subject?: Subject | null, jobKey?: string | null): LookImage | null {
    if (!subject) return null;

    // `presentToken` normalizes blank strings to null before each `??` so a blank
    // token is treated as absent and can't short-circuit an available fallback
    // (reorder/proof sets subject.bearerTokenURL to '' when it has no token).
    const subjectToken = this.presentToken(subject.bearerTokenURL) ?? this.resolveFallbackToken(jobKey);

    for (const candidate of this.candidates(subject, jobKey)) {
      const look = this.toLookImage(candidate.viewURL, this.presentToken(candidate.bearerTokenURL) ?? subjectToken);
      if (look) return look;
    }

    return null;
  }

  /** A blank bearer token is treated as absent (null) so fallbacks still fire. */
  private presentToken(token?: string | null): string | null {
    // Not `??`: the whole point is to map '' (a falsy non-nullish value) to null.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return token || null;
  }

  /**
   * Token fallbacks, tried in order, for when the winning image source carries no
   * token of its own. All rungs ultimately resolve to the same session-scoped IRIS
   * `ImageAuthorization` token (one per master subject / job), so this is a
   * robustness net rather than a source of divergence.
   */
  private resolveFallbackToken(jobKey?: string | null): string | null {
    return (
      // 1) Selected subject's token, else the *active* job's imageAuthorization
      //    (SubjectStore.getBearerTokenURL uses JobStore.getJob() — no jobKey).
      this.presentToken(this.subjectStore.getBearerTokenURL()) ??
      // 2) The jobKey-scoped stored job's imageAuthorization token. Only reached
      //    when (1) is empty (no selected subject and no active job); the jobKey
      //    argument therefore only matters in that degenerate case.
      this.presentToken(this.jobStore.getJobData(jobKey)?.imageAuthorization?.bearerTokenURL) ??
      null
    );
  }

  /**
   * Ordered image sources, highest priority first. Each yields a raw
   * (url, token) pair; `resolve` picks the first that produces a valid look and
   * falls back to the subject-level bearer token when a source has none.
   *
   * Backend sites referenced below (both go through IRIS
   * `imageRetrievalServiceClient.prepare`):
   * - Site #1 — per-subject portrait. `AvailableLooksService.setPreviewImageUrls`
   *   (called via `MasterSubjectService.prepareReorderImages`) renders one image per
   *   subject keyed `L_${defaultLookLayoutThemeId}_${captureSessionKey}` and sets
   *   `masterEvents[].subjectInfo.previewImageUrl`
   * - Site #2 — look-picker grid. `AvailableLooksService.getAvailableLooks` renders
   *   per layout theme (keyed `L_${layoutThemeID}`) into `jobData.availableLooks`;
   *   surfaced client-side via `LooksStore`.
   */
  private candidates(subject: Subject, jobKey?: string | null): LookCandidate[] {
    const sources = [
      // The master event for the job currently in view. jobKey === jobNumber
      // (JobStore.updateJobData), so we match the subject's own master events directly:
      // deterministic and leak-proof (only ever reads this subject's events). Yields nothing in
      // reorder/proof, where jobKey is portraitId+accessCode rather than a jobNumber — the
      // denormalized override below carries the image in that flow.
      masterEventLookByJobKey(subject.masterEvents, jobKey),
      // The user's chosen event. Comes from the master subject events; nulled on fallback
      // events by SubjectStore.setRelatedMasterEventToAllSubjects to avoid cross-subject leakage.
      subject.selectedEvent?.subjectInfo,
      // Denormalized subject-level portrait. Normally the first master event with an image
      // (via SubjectStore.getLatestPreviewImageUrlFromMasterEvents), but overwritten with
      // data from the JobController in the reorder/proof flow.
      { previewImageUrl: subject.previewImageUrl, bearerTokenURL: subject.bearerTokenURL },
      // Raw source of truth: first eligible master event portrait. Pure master subject data.
      firstMasterEventLook(subject.masterEvents)
    ];

    return sources.map((source) => ({
      viewURL: source?.previewImageUrl,
      bearerTokenURL: source?.bearerTokenURL
    }));
  }

  private toLookImage(viewURL?: string | null, bearerTokenURL?: string | null): LookImage | null {
    if (!isIrisUrl(viewURL) || !bearerTokenURL) {
      return null;
    }

    return {
      viewURL,
      bearerTokenURL,
      orientation: 'PORTRAIT'
    };
  }
}

App.factory('SubjectLookResolver', downgradeInjectable(SubjectLookResolver));
